// SHELVED FEATURE — this page is intentionally not linked from the admin
// menu (config/admin/menu.js). Athlete Groups let clubs create arbitrary
// athlete groupings and assign them to a meet event program, but age groups
// turned out to cover the same need. The feature is kept fully working
// rather than deleted: this page is still reachable at
// routes.admin.clubSettings.athleteGroups, and existing athlete groups
// continue to work in the program designer and volunteer screens.
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import MetaTags from 'components/common/MetaTags';
import AdminDataTable from 'components/admin/AdminDataTable';
import MainContent from 'components/NewDesign/MainContent';
import PageHeader from 'components/NewDesign/PageHeader';
import {
  deleteClubAthleteGroupById,
  getClubAthleteGroups,
} from 'api/club/athleteGroupApi';
import CustomNewButton from 'components/NewDesign/CustomNewButton/CustomNewButton';
import styles from './AthleteGroups.module.css';
import AthleteGroupEditPopup from 'components/admin/ClubSettings/AthleteGroupEditPopup';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { getErrorMessage } from 'utils/api';
import { toast } from 'react-toastify';
import TableActionButtons from 'components/NewDesign/TableActionButtons';

const athleteGroupColumns = (onEdit, onDelete) => [
  {
    accessor: 'groupName',
    label: 'Group Name',
  },
  {
    accessor: 'baseAgeRange',
    label: 'Age Range',
  },
  {
    accessor: 'memberCount',
    label: 'Number of Members',
  },
  {
    accessor: 'sortOrder',
    label: 'Sort Order',
  },
  {
    accessor: 'visibleText',
    label: 'Visible',
  },
  {
    accessor: 'actions',
    label: 'Actions',
    Cell: ({ row }) => TableActionButtons(row, onEdit, onDelete),
  },
];

const AthleteGroups = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const [athleteGroups, setAthleteGroups] = useState([]);
  const [fetchingGroups, setFetchingGroups] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState();
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editMode, setEditMode] = useState();

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAthleteGroups = useCallback(async () => {
    try {
      setFetchingGroups(true);
      const response = await getClubAthleteGroups(
        selectedClub?.clubID,
        false,
        true
      );
      if (response.data) {
        setAthleteGroups(
          response.data.map((group) => {
            return {
              ...group,
              visibleText: group.isActive ? 'Yes' : 'No',
            };
          })
        );
      }
    } catch (err) {
      console.log('[AthleteGroups] Fetch athlete groups error: ', err);
    }
  }, [selectedClub?.clubID]);

  useEffect(() => {
    fetchAthleteGroups().then(() => {
      setFetchingGroups(false);
    });
  }, [fetchAthleteGroups]);

  const handleAddAthleteGroup = () => {
    setShowEditPopup(true);
    setEditMode('add');
  };

  const handleEditAthleteGroup = (group) => {
    setShowEditPopup(true);
    setEditMode('edit');
    setSelectedGroup(group);
  };

  const handleDeleteAthleteGroup = (group) => {
    setSelectedGroup(group);
    setShowDeleteConfirm(true);
  };

  const handleEditPopupClose = async (refresh) => {
    setShowEditPopup(false);
    setEditMode(null);
    setSelectedGroup(null);
    if (refresh) {
      await fetchAthleteGroups();
      setFetchingGroups(false);
    }
  };

  const confirmDeleteGroup = async () => {
    try {
      if (!selectedClub?.clubID) {
        return;
      }
      setIsDeleting(true);
      await deleteClubAthleteGroupById(
        selectedClub.clubID,
        selectedGroup.athleteGroupID
      );
      await fetchAthleteGroups();
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setSelectedGroup(null);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setFetchingGroups(false);
    }
  };

  const tableColumns = athleteGroupColumns(
    handleEditAthleteGroup,
    handleDeleteAthleteGroup
  );

  return (
    <>
      <MetaTags title="SplitFast | Athlete Groups" />
      <MainContent loading={fetchingGroups}>
        <PageHeader title="Athlete Groups">
          <div className={styles.buttonContainer}>
            <CustomNewButton
              label="+ Add Athlete Group"
              onClick={handleAddAthleteGroup}
              style={{ marginLeft: 'auto' }}
              variant="primary"
            />
          </div>
        </PageHeader>
        <AdminDataTable
          columns={tableColumns}
          data={athleteGroups}
          searchInputPlaceholder="Search Athlete Groups"
          headStyle={{
            gridTemplateColumns: '3fr 3fr 2fr 2fr 2fr 2fr', // group name, age range, number of members, sort order, visible, actions
          }}
          rowStyle={{
            gridTemplateColumns: '3fr 3fr 2fr 2fr 2fr 2fr', // group name, age range, number of members, sort order, visible, actions
          }}
        />
        <AthleteGroupEditPopup
          clubID={selectedClub?.clubID}
          isOpen={showEditPopup}
          onClose={(e) => handleEditPopupClose(e)}
          group={selectedGroup}
          mode={editMode}
        ></AthleteGroupEditPopup>
        {/* Delete Confirmation Dialog */}
        <CustomNewConfirm
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setSelectedGroup(null);
          }}
          title="Delete Athlete Group"
          description="Are you sure you want to delete this athlete group?"
          confirmButtonText="Delete Athlete Group"
          confirmButtonVariant="danger"
          onConfirm={confirmDeleteGroup}
          cancelButtonText="Cancel"
          showWarning={true}
          warningText="This action cannot be undone. The athlete group will be permanently removed"
          isLoading={isDeleting}
        />
      </MainContent>
    </>
  );
};

export default AthleteGroups;
